import React, { useEffect, useRef, useState, useCallback } from 'react';
import { StudyNote } from '../types';
import { saveMapPositions, loadMapPositions, NodePositions, clearMapPositions } from '../utils/storage';

interface Node {
  id: string;
  label: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  isMain: boolean;
  noteId: string;
  verseText?: string;
}

interface Edge {
  source: string;
  target: string;
  noteId: string;
}

interface Props {
  notes: StudyNote[];
  onSelectNote?: (noteId: string) => void;
}

function buildGraph(notes: StudyNote[]): { nodes: Node[]; edges: Edge[]; sharedNodeIds: Set<string> } {
  const nodeMap = new Map<string, Node>();
  const edges: Edge[] = [];
  const width = 1800;
  const height = 700;

  const addNode = (id: string, label: string, isMain: boolean, noteId: string) => {
    if (!nodeMap.has(id)) {
      nodeMap.set(id, {
        id,
        label,
        x: Math.random() * (width - 300) + 150,
        y: Math.random() * (height - 200) + 100,
        vx: 0,
        vy: 0,
        isMain,
        noteId,
      });
    }
  };

  for (const note of notes) {
    addNode(note.verse.id, note.verse.display, true, note.id);
    const mainNode = nodeMap.get(note.verse.id)!;
    if (!mainNode.verseText && note.verse.verseText) mainNode.verseText = note.verse.verseText;
    for (const linked of note.linkedVerses) {
      addNode(linked.id, linked.display, false, note.id);
      const linkedNode = nodeMap.get(linked.id)!;
      if (!linkedNode.verseText && linked.verseText) linkedNode.verseText = linked.verseText;
      edges.push({ source: note.verse.id, target: linked.id, noteId: note.id });
      for (const child of linked.linkedVerses || []) {
        addNode(child.id, child.display, false, note.id);
        const childNode = nodeMap.get(child.id)!;
        if (!childNode.verseText && child.verseText) childNode.verseText = child.verseText;
        edges.push({ source: linked.id, target: child.id, noteId: note.id });
      }
    }
  }

  // Track how many distinct main verses link to each non-main node
  const mainSourcesPerNode = new Map<string, Set<string>>();
  for (const note of notes) {
    for (const linked of note.linkedVerses) {
      if (!mainSourcesPerNode.has(linked.id)) mainSourcesPerNode.set(linked.id, new Set());
      mainSourcesPerNode.get(linked.id)!.add(note.verse.id);
    }
  }
  const sharedNodeIds = new Set(
    [...mainSourcesPerNode.entries()].filter(([, s]) => s.size > 1).map(([id]) => id)
  );

  return { nodes: Array.from(nodeMap.values()), edges, sharedNodeIds };
}

const IDEAL_EDGE_LEN = 160;
const REPULSION = 4000;
const DAMPING = 0.7;
const ITERATIONS = 120;

function runLayout(nodes: Node[], edges: Edge[]): Node[] {
  const ns = nodes.map(n => ({ ...n }));

  for (let iter = 0; iter < ITERATIONS; iter++) {
    // Repulsion between all pairs
    for (let i = 0; i < ns.length; i++) {
      for (let j = i + 1; j < ns.length; j++) {
        const dx = ns[j].x - ns[i].x;
        const dy = ns[j].y - ns[i].y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
        const force = REPULSION / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        ns[i].vx -= fx;
        ns[i].vy -= fy;
        ns[j].vx += fx;
        ns[j].vy += fy;
      }
    }

    // Attraction along edges
    for (const edge of edges) {
      const s = ns.find(n => n.id === edge.source);
      const t = ns.find(n => n.id === edge.target);
      if (!s || !t) continue;
      const dx = t.x - s.x;
      const dy = t.y - s.y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const force = (dist - IDEAL_EDGE_LEN) * 0.05;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      s.vx += fx;
      s.vy += fy;
      t.vx -= fx;
      t.vy -= fy;
    }

    // Update positions
    for (const n of ns) {
      n.vx *= DAMPING;
      n.vy *= DAMPING;
      n.x += n.vx;
      n.y += n.vy;
      n.x = Math.max(80, Math.min(1720, n.x));
      n.y = Math.max(40, Math.min(660, n.y));
    }
  }

  return ns;
}

export const CrossReferenceMap: React.FC<Props> = ({ notes, onSelectNote }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [sharedNodeIds, setSharedNodeIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const { nodes: rawNodes, edges: rawEdges, sharedNodeIds: sharedIds } = buildGraph(notes);
    const laid = runLayout(rawNodes, rawEdges);
    loadMapPositions().then(saved => {
      setNodes(laid.map(n => saved[n.id] ? { ...n, x: saved[n.id].x, y: saved[n.id].y } : n));
    });
    setEdges(rawEdges);
    setSharedNodeIds(sharedIds);
    setSelected(null);
  }, [notes]);

  const getConnectedIds = useCallback((nodeId: string) => {
    const ids = new Set<string>([nodeId]);
    for (const e of edges) {
      if (e.source === nodeId) ids.add(e.target);
      if (e.target === nodeId) ids.add(e.source);
    }
    return ids;
  }, [edges]);

  const handleNodeMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    const node = nodes.find(n => n.id === nodeId)!;
    setDragging(nodeId);
    setDragOffset({ x: svgP.x - node.x, y: svgP.y - node.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    setNodes(prev => prev.map(n =>
      n.id === dragging
        ? { ...n, x: Math.max(80, Math.min(1720, svgP.x - dragOffset.x)), y: Math.max(40, Math.min(660, svgP.y - dragOffset.y)) }
        : n
    ));
  };

  const handleMouseUp = (e: React.MouseEvent, nodeId?: string) => {
    if (dragging && nodeId === dragging) {
      setSelected(prev => prev === nodeId ? null : nodeId!);
      setExpandedNodes(prev => {
        const next = new Set(prev);
        if (next.has(nodeId!)) next.delete(nodeId!); else next.add(nodeId!);
        return next;
      });
      if (nodeId) {
        const node = nodes.find(n => n.id === nodeId);
        if (node && onSelectNote) onSelectNote(node.noteId);
      }
    }
    if (dragging) {
      setNodes(prev => {
        const positions: NodePositions = {};
        prev.forEach(n => { positions[n.id] = { x: n.x, y: n.y }; });
        saveMapPositions(positions);
        return prev;
      });
    }
    setDragging(null);
  };

  const handleResetLayout = async () => {
    await clearMapPositions();
    const { nodes: rawNodes, edges: rawEdges, sharedNodeIds: sharedIds } = buildGraph(notes);
    setNodes(runLayout(rawNodes, rawEdges));
    setEdges(rawEdges);
    setSharedNodeIds(sharedIds);
    setSelected(null);
    setExpandedNodes(new Set());
  };

  const connectedIds = selected ? getConnectedIds(selected) : null;

  if (notes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        No notes yet. Add some notes to see the cross-reference map.
      </div>
    );
  }

  if (nodes.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {nodes.length} verse{nodes.length !== 1 ? 's' : ''} · {edges.length} connection{edges.length !== 1 ? 's' : ''}
          {selected && <span className="ml-3 text-blue-600 font-medium">Click again or elsewhere to deselect</span>}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setExpandedNodes(new Set(nodes.filter(n => n.verseText).map(n => n.id)))}
            className="px-3 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
          >
            Show All Verses
          </button>
          <button
            onClick={() => setExpandedNodes(new Set())}
            className="px-3 py-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
          >
            Hide All
          </button>
          <button
            onClick={handleResetLayout}
            className="px-3 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
          >
            Reset Layout
          </button>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-600"></span> Main verse</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-indigo-400"></span> Linked verse</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-amber-400"></span> Shared by multiple</span>
            <span>Drag · Click to connect</span>
          </div>
        </div>
      </div>
      <svg
        ref={svgRef}
        viewBox="0 0 1800 700"
        className="w-full"
        style={{ height: 600 }}
        onMouseMove={handleMouseMove}
        onMouseUp={() => setDragging(null)}
        onClick={(e) => { if (e.target === svgRef.current) setSelected(null); }}
      >
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="16" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#94a3b8" />
          </marker>
          <marker id="arrow-active" markerWidth="8" markerHeight="8" refX="16" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#3b82f6" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge, i) => {
          const s = nodes.find(n => n.id === edge.source);
          const t = nodes.find(n => n.id === edge.target);
          if (!s || !t) return null;
          const isActive = connectedIds
            ? connectedIds.has(edge.source) && connectedIds.has(edge.target)
            : false;
          const dimmed = connectedIds && !isActive;
          return (
            <line
              key={i}
              x1={s.x} y1={s.y}
              x2={t.x} y2={t.y}
              stroke={isActive ? '#3b82f6' : '#cbd5e1'}
              strokeWidth={isActive ? 2 : 1}
              strokeOpacity={dimmed ? 0.2 : 1}
              markerEnd={isActive ? 'url(#arrow-active)' : 'url(#arrow)'}
            />
          );
        })}

        {/* Nodes */}
        {nodes.map(node => {
          const isSelected = selected === node.id;
          const isConnected = connectedIds ? connectedIds.has(node.id) : false;
          const dimmed = connectedIds && !isConnected;
          const r = node.isMain ? 28 : 22;
          const maxLabelLen = 16;
          const label = node.label.length > maxLabelLen ? node.label.slice(0, maxLabelLen - 1) + '…' : node.label;

          return (
            <g
              key={node.id}
              transform={`translate(${node.x},${node.y})`}
              style={{ cursor: dragging === node.id ? 'grabbing' : 'grab', opacity: dimmed ? 0.25 : 1 }}
              onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
              onMouseUp={(e) => handleMouseUp(e, node.id)}
            >
              <circle
                r={r}
                fill={
                  node.isMain
                    ? (isSelected ? '#1d4ed8' : '#3b82f6')
                    : sharedNodeIds.has(node.id)
                      ? (isSelected ? '#b45309' : '#f59e0b')
                      : (isSelected ? '#4338ca' : '#818cf8')
                }
                stroke={isSelected || isConnected ? '#1e40af' : '#e2e8f0'}
                strokeWidth={isSelected ? 3 : isConnected ? 2 : 1}
              />
              <text
                textAnchor="middle"
                dy="0.35em"
                fontSize={node.isMain ? 9 : 8}
                fill="white"
                fontWeight={node.isMain ? 'bold' : 'normal'}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {label}
              </text>
              {expandedNodes.has(node.id) && node.verseText && (() => {
                const popupW = 240;
                const popupH = 180;
                // All coords are relative to node center (g is already translated)
                const flipLeft = node.x + r + popupW + 8 > 1780;
                const flipUp = node.y + popupH / 2 > 660;
                const px = flipLeft ? -popupW - r - 4 : r + 4;
                const py = flipUp ? -popupH : -popupH / 2;
                return (
                  <foreignObject
                    x={px}
                    y={py}
                    width={popupW}
                    height={popupH}
                    style={{ pointerEvents: 'all', overflow: 'visible' }}
                  >
                    <div
                      style={{
                        background: 'white',
                        border: '1px solid #cbd5e1',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 11,
                        color: '#1e293b',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        lineHeight: 1.5,
                        maxHeight: popupH,
                        overflowY: 'auto',
                      } as React.CSSProperties}
                    >
                      <span style={{ fontWeight: 600, color: '#3b82f6', display: 'block', marginBottom: 2 }}>{node.label}</span>
                      {node.verseText}
                    </div>
                  </foreignObject>
                );
              })()}
            </g>
          );
        })}
      </svg>
    </div>
  );
};
